import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { TrendingUp, Target, Trophy, Users, Award, Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Statistik = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedEventId = searchParams.get('eventId');
  const [categoryFilter, setCategoryFilter] = useState('U-8');
  const [eventCategories, setEventCategories] = useState([]);
  const [playerStats, setPlayerStats] = useState([]);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('goals');

  useEffect(() => {
    if (!selectedEventId) {
      navigate(`/event?next=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [selectedEventId, navigate, location.pathname]);

  useEffect(() => {
    const loadEventMeta = async () => {
      if (!selectedEventId) return;
      try {
        const response = await apiService.getPublicEventById(selectedEventId);
        const categories = response?.data?.event?.categories || [];
        setEventCategories(categories);
        if (categories.length > 0 && !categories.includes(categoryFilter)) {
          setCategoryFilter(categories[0]);
        }
      } catch (error) {
        setEventCategories([]);
      }
    };
    loadEventMeta();
  }, [selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    loadData();
  }, [categoryFilter, selectedEventId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, matchesRes, standingsRes] = await Promise.all([
        apiService.getPlayerStats(categoryFilter, 'goals', selectedEventId),
        apiService.getMatches(null, categoryFilter, selectedEventId),
        apiService.getStandings(categoryFilter, selectedEventId)
      ]);
      setPlayerStats(statsRes.data || []);
      setMatches(matchesRes.data || []);
      setStandings(standingsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const finishedMatches = matches.filter(m => m.status === 'finished');
  const totalMatches = finishedMatches.length;
  const totalGoals = finishedMatches.reduce((sum, m) => sum + (m.home_score || 0) + (m.away_score || 0), 0);
  const avgGoalsPerMatch = totalMatches > 0 ? (totalGoals / totalMatches).toFixed(2) : 0;
  const totalTeams = standings.length;

  // Sort players by different criteria
  const sortedByGoals = [...playerStats].sort((a, b) => (b.goals || 0) - (a.goals || 0));
  const sortedByAssists = [...playerStats].sort((a, b) => (b.assists || 0) - (a.assists || 0));
  const sortedByTotal = [...playerStats].sort((a, b) => {
    const totalA = (a.goals || 0) + (a.assists || 0);
    const totalB = (b.goals || 0) + (b.assists || 0);
    return totalB - totalA;
  });

  const getMedalStyle = (index) => {
    if (index === 0) return 'bg-yellow-400 text-yellow-900';
    if (index === 1) return 'bg-gray-300 text-gray-700';
    if (index === 2) return 'bg-orange-400 text-orange-900';
    return 'bg-green-100 text-green-700';
  };

  const renderPlayerCard = (player, index, showField) => {
    const total = (player.goals || 0) + (player.assists || 0);
    return (
      <div 
        key={player.id || index} 
        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${getMedalStyle(index)}`}>
            {index + 1}
          </div>
          <div className="flex items-center space-x-3">
            {player.photo ? (
              <img src={player.photo} alt={player.name} className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-green-200 flex items-center justify-center">
                <span className="text-green-700 font-bold text-lg">{player.name?.charAt(0)}</span>
              </div>
            )}
            <div>
              <h3 className="font-semibold text-gray-900">{player.name}</h3>
              <p className="text-sm text-gray-600">{player.team}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-6">
          {showField === 'all' && (
            <>
              <div className="text-center">
                <p className="text-xl font-bold text-green-600">{player.goals || 0}</p>
                <p className="text-xs text-gray-500">Gol</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-blue-600">{player.assists || 0}</p>
                <p className="text-xs text-gray-500">Assist</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-purple-600">{total}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </>
          )}
          {showField === 'goals' && (
            <div className="text-right">
              <p className="text-2xl font-bold text-green-600">{player.goals || 0}</p>
              <p className="text-xs text-gray-500">gol</p>
            </div>
          )}
          {showField === 'assists' && (
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">{player.assists || 0}</p>
              <p className="text-xs text-gray-500">assist</p>
            </div>
          )}
          {showField === 'total' && (
            <div className="text-right">
              <p className="text-2xl font-bold text-purple-600">{total}</p>
              <p className="text-xs text-gray-500">G+A</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!selectedEventId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="flex items-center space-x-3 mb-8">
          <TrendingUp className="h-10 w-10 text-green-600" />
          <h1 className="text-4xl font-bold text-gray-900">Statistik Liga</h1>
        </div>

        {/* Category Filter */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kategori
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            {(eventCategories.length > 0 ? eventCategories : ['U-8', 'U-10', 'U-12', 'U-14']).map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Total Pertandingan</h3>
              <Trophy className="h-8 w-8 opacity-80" />
            </div>
            <p className="text-4xl font-bold">{totalMatches}</p>
            <p className="text-blue-100 text-sm mt-2">Pertandingan selesai</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Total Gol</h3>
              <Target className="h-8 w-8 opacity-80" />
            </div>
            <p className="text-4xl font-bold">{totalGoals}</p>
            <p className="text-green-100 text-sm mt-2">Gol tercipta</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Rata-rata Gol</h3>
              <TrendingUp className="h-8 w-8 opacity-80" />
            </div>
            <p className="text-4xl font-bold">{avgGoalsPerMatch}</p>
            <p className="text-purple-100 text-sm mt-2">Per pertandingan</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Total Tim</h3>
              <Users className="h-8 w-8 opacity-80" />
            </div>
            <p className="text-4xl font-bold">{totalTeams}</p>
            <p className="text-orange-100 text-sm mt-2">Tim peserta</p>
          </div>
        </div>

        {/* Player Statistics with Tabs */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Statistik Pemain</h2>
          
          <Tabs defaultValue="goals" className="w-full" onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="goals" className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                Top Skor
              </TabsTrigger>
              <TabsTrigger value="assists" className="flex items-center gap-2">
                <Award className="h-4 w-4" />
                Top Assist
              </TabsTrigger>
              <TabsTrigger value="total" className="flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                Top Kontribusi
              </TabsTrigger>
            </TabsList>

            <TabsContent value="goals">
              <div className="space-y-4">
                {sortedByGoals.slice(0, 10).map((player, index) => renderPlayerCard(player, index, 'goals'))}
                {sortedByGoals.length === 0 && (
                  <p className="text-center text-gray-500 py-8">Belum ada data statistik</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="assists">
              <div className="space-y-4">
                {sortedByAssists.slice(0, 10).map((player, index) => renderPlayerCard(player, index, 'assists'))}
                {sortedByAssists.length === 0 && (
                  <p className="text-center text-gray-500 py-8">Belum ada data statistik</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="total">
              <div className="space-y-4">
                {sortedByTotal.slice(0, 10).map((player, index) => renderPlayerCard(player, index, 'all'))}
                {sortedByTotal.length === 0 && (
                  <p className="text-center text-gray-500 py-8">Belum ada data statistik</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Top 5 Teams */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Top 5 Tim</h2>
          <div className="space-y-4">
            {standings.slice(0, 5).map((team, index) => (
              <div key={team.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${getMedalStyle(index)}`}>
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{team.team}</h3>
                    <p className="text-sm text-gray-600">{team.played} pertandingan</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{team.won}</p>
                    <p className="text-xs text-gray-500">M</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-600">{team.drawn}</p>
                    <p className="text-xs text-gray-500">S</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-red-600">{team.lost}</p>
                    <p className="text-xs text-gray-500">K</p>
                  </div>
                  <div className="text-center border-l pl-4">
                    <p className="text-2xl font-bold text-green-600">{team.points}</p>
                    <p className="text-xs text-gray-500">poin</p>
                  </div>
                </div>
              </div>
            ))}
            {standings.length === 0 && (
              <p className="text-center text-gray-500 py-8">Belum ada data klasemen</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistik;
